import { useState, useEffect, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { AuthContext } from "../context/AuthContext"; // tu contexto de auth
import api from "../utils/Api"; // axios configurado con baseURL

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// Formulario de pago
function CheckoutForm({ token, onSuccess, preview }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!stripe || !elements) {
      setMessage("Stripe no está listo");
      setLoading(false);
      return;
    }

    try {
      const card = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
      });

      if (error) {
        setMessage(error.message);
        setLoading(false);
        return;
      }

      // Crear suscripción en backend
      const res = await api.post(
        "/subscribe",
        { payment_method: paymentMethod.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("Suscripción creada con éxito");
      onSuccess();
    } catch (err) {
      setMessage(err.response?.data?.message || "Error al suscribirse");
      console.error(err.response || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubscribe} style={{ maxWidth: 400 }}>
      {preview && (
        <div style={{ marginBottom: 10 }}>
          <p>Empleados activos: {preview.employees}</p>
          <p>Precio por empleado: {preview.price_per_employee} €/mes</p>
          <p>Total mensual: {preview.total} €</p>
          <p>Trial disponible: {preview.trial_days} días</p>
        </div>
      )}
      <CardElement options={{ hidePostalCode: true }} />
      <button type="submit" disabled={loading} style={{ marginTop: 10 }}>
        {loading ? "Procesando..." : "Suscribirse"}
      </button>
      {message && <p style={{ marginTop: 10 }}>{message}</p>}
    </form>
  );
}

// Componente principal de suscripción
export default function Subscribe() {
  const { token } = useContext(AuthContext);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [preview, setPreview] = useState(null);

  // Chequear estado de suscripción
  const checkSubscription = async () => {
    try {
      const res = await api.get("/subscribe/check", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionInfo({
        active: res.data.subscribed,
        trialEndsAt: res.data.trial_ends_at,
        endsAt: res.data.ends_at,
        status: res.data.status,
      });
    } catch (err) {
      console.error("Error al chequear suscripción:", err.response || err);
    }
  };

  // Obtener preview de suscripción
  const fetchPreview = async () => {
    try {
      const res = await api.get("/subscribe/preview", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch (err) {
      console.error("Error al obtener preview:", err.response || err);
    }
  };

  // Cancelar suscripción
  const handleCancel = async () => {
    setLoadingCancel(true);
    try {
      const res = await api.post(
        "/subscribe/cancel",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      checkSubscription();
    } catch (err) {
      const message =
        err.response?.data?.message || "Error desconocido al cancelar la suscripción";
      alert(message);
      console.error("Error al cancelar:", err.response || err);
    } finally {
      setLoadingCancel(false);
    }
  };

  useEffect(() => {
    if (token) {
      checkSubscription();
      fetchPreview();
    }
  }, [token]);

  return (
    <div>
      {subscriptionInfo?.active ? (
        <div>
          <p>Suscripción activa</p>
          {subscriptionInfo.trialEndsAt && (
            <p>Trial termina: {new Date(subscriptionInfo.trialEndsAt).toLocaleDateString()}</p>
          )}
          {subscriptionInfo.endsAt && (
            <p>Fin de suscripción: {new Date(subscriptionInfo.endsAt).toLocaleDateString()}</p>
          )}
          <button
            onClick={handleCancel}
            disabled={loadingCancel || subscriptionInfo.status === "canceled"}
          >
            {loadingCancel ? "Procesando..." : "Cancelar suscripción"}
          </button>
        </div>
      ) : (
        <Elements stripe={stripePromise}>
          <CheckoutForm token={token} onSuccess={checkSubscription} preview={preview} />
        </Elements>
      )}
    </div>
  );
}
